/**
 * v4.30.1 — Live preview banner.
 *
 * Calm slate strip across the very top of the authenticated shell
 * that appears only while an admin is in "Preview as organization"
 * mode. Two responsibilities:
 *   1. Inform — make the impersonation visible so the operator
 *      doesn't forget they're seeing someone else's branding.
 *   2. Exit — single click returns the shell to the operator's
 *      real branding.
 *
 * Style discipline:
 *   - NO red, NO amber, NO warning energy.
 *   - Slate-neutral background, calm copy, single subtle accent dot
 *     on the left to show it's a live preview, not a real banner.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    clearPreviewOverride,
    getPreviewOverride,
    isPreviewOverrideActive,
} from '../../lib/branding';

export const PreviewBanner = () => {
    const { t } = useTranslation();
    const [active, setActive] = useState(() => isPreviewOverrideActive());
    const [payload, setPayload] = useState(() => getPreviewOverride());

    useEffect(() => {
        const sync = () => {
            setActive(isPreviewOverrideActive());
            setPayload(getPreviewOverride());
        };
        window.addEventListener('ob:preview-override-changed', sync);
        return () => window.removeEventListener('ob:preview-override-changed', sync);
    }, []);

    if (!active) return null;

    const label = payload?.organization?.display_name
        || payload?.organization?.name
        || payload?.brand_name
        || t('portalIdentity.banner.defaultOrgLabel');

    const handleExit = () => {
        clearPreviewOverride();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ob:preview-override-changed'));
        }
    };

    const prefix = t('portalIdentity.banner.previewingPrefix');
    const suffix = t('portalIdentity.banner.previewingSuffix');

    return (
        <div
            className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 px-4 py-1.5 bg-slate-800 dark:bg-slate-950 text-slate-200 text-xs"
            style={{ letterSpacing: '0.02em' }}
            data-testid="preview-banner"
        >
            <span
                className="inline-block rounded-full"
                style={{ width: 6, height: 6, background: '#10b981' }}
                aria-hidden="true"
            />
            <span>
                {prefix} <span className="font-semibold text-white">{label}</span>
                {suffix ? <> {suffix}</> : null}
            </span>
            <button
                type="button"
                onClick={handleExit}
                className="ml-2 px-2.5 py-0.5 rounded-md border border-slate-600 hover:border-slate-400 text-slate-200 hover:text-white text-[11px]"
                data-testid="preview-banner-exit"
            >
                {t('portalIdentity.banner.exit')}
            </button>
        </div>
    );
};

export default PreviewBanner;
